import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Layers, Plus, Trash2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  generateTemporaryStructuresWordDocument,
  STRUCTURE_CONTROL_LABELS,
  STRUCTURE_DOC_LABELS,
  STRUCTURE_TYPE_LABELS,
} from "@/lib/documentGenerator/basuWordGenerators";

interface Row {
  id: string;
  name: string;
  structure_type: string;
  category: string | null;
  description: string | null;
  supplier: string | null;
  competent_person: string | null;
  competence_verified: boolean;
  documentation_status: string;
  control_status: string;
  source_role: string | null;
  flagged_to_byggherre: boolean;
  note: string | null;
}

const CATEGORIES = [
  "Ställning",
  "Formställning",
  "Stämp och bockrygg",
  "Spont och schaktstöd",
  "Lyftanordning/lyftplan",
  "Väderskydd",
  "Tillfällig el",
  "Övrigt",
];

export const TemporaryStructures: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [{ data }, { data: proj }] = await Promise.all([
      supabase
        .from("temporary_structures")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
    ]);
    setProjectName(proj?.name ?? "");
    setRows((data as unknown as Row[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const add = async () => {
    const { error } = await supabase.from("temporary_structures").insert({
      project_id: projectId,
      name: "Ny konstruktion",
      sort_order: rows.length,
    });
    if (error) return toast.error("Kunde inte lägga till");
    load();
  };

  const update = async (id: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase.from("temporary_structures").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("temporary_structures").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const exportWord = async () => {
    try {
      await generateTemporaryStructuresWordDocument(rows, projectName);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const missing = rows.filter((r) => !r.competence_verified || r.documentation_status === "saknas").length;

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="44"
        exportHistory={{ documentType: "tillfalliga_konstruktioner" }}
        title="Tillfälliga och samverkande konstruktioner"
        description="Ställningar, form, stämp, spont och andra konstruktioner som bär last under bygget. Kräver utsedd kompetent person, handlingar och kontroll före användning."
        legalRef="AFS 2023:13 kap. 5 · AFS 2023:3 kap. 2 § 17"
        icon={Layers}
        meta={
          rows.length ? (
            <Badge variant={missing ? "destructive" : "outline"}>
              {missing ? `${missing} saknar kompetens eller handlingar` : "Alla kontrollerade"}
            </Badge>
          ) : null
        }
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={add}>
              <Plus className="mr-1 h-4 w-4" /> Lägg till
            </Button>
            {rows.length ? (
              <Button size="sm" variant="outline" onClick={exportWord}>
                <Download className="mr-1 h-4 w-4" /> Word
              </Button>
            ) : null}
          </div>
        }
      />

      {!rows.length ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga konstruktioner registrerade. Lägg in ställningar, form, stämp och spont så att kompetens
            och handlingar kan följas upp innan de tas i bruk.
          </CardContent>
        </Card>
      ) : (
        rows.map((row) => (
          <Card key={row.id}>
            <CardContent className="grid gap-4 pt-6 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <div className="flex items-center gap-2">
                  <Input
                    className="font-medium"
                    defaultValue={row.name}
                    onBlur={(e) => update(row.id, { name: e.target.value })}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Ta bort konstruktionen"
                    onClick={() => remove(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select
                  value={row.structure_type}
                  onValueChange={(v) => update(row.id, { structure_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STRUCTURE_TYPE_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select
                  value={row.category ?? ""}
                  onValueChange={(v) => update(row.id, { category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Välj kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Leverantör / utförare</Label>
                <Input
                  defaultValue={row.supplier ?? ""}
                  onBlur={(e) => update(row.id, { supplier: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Kompetent person</Label>
                <Input
                  defaultValue={row.competent_person ?? ""}
                  onBlur={(e) => update(row.id, { competent_person: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Konstruktionshandlingar</Label>
                <Select
                  value={row.documentation_status}
                  onValueChange={(v) => update(row.id, { documentation_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STRUCTURE_DOC_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kontroll före användning</Label>
                <Select
                  value={row.control_status}
                  onValueChange={(v) => update(row.id, { control_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STRUCTURE_CONTROL_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch
                  checked={row.competence_verified}
                  onCheckedChange={(v) => update(row.id, { competence_verified: v })}
                  id={`komp-${row.id}`}
                />
                <Label htmlFor={`komp-${row.id}`}>Kompetens verifierad av Bas-U</Label>
                <Switch
                  checked={row.flagged_to_byggherre}
                  onCheckedChange={(v) => update(row.id, { flagged_to_byggherre: v })}
                  id={`flag-${row.id}`}
                />
                <Label htmlFor={`flag-${row.id}`}>Brist meddelad byggherren</Label>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Kommentar</Label>
                <Textarea
                  rows={2}
                  defaultValue={row.note ?? ""}
                  onBlur={(e) => update(row.id, { note: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default TemporaryStructures;
